/**
 * Debug HUD for builder/preview state. Shown when ?debug=1.
 * Displays current state and provides Copy Debug JSON for support.
 * When inline=true, renders without fixed positioning for embedding in a panel.
 */
import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";

export type BuildLogItem = {
  id: string;
  ts: number;
  status: string;
  prompt?: string;
  summary?: string;
  error?: string;
};

export type BuilderEvent = { ts: number; type: string; data?: unknown };

export type FileTargetItem = { path: string; action: string; rationale?: string };

export type BuilderDebugHUDProps = {
  builderStatus: string;
  waitingForScaffold: boolean;
  loadedFileContentsSize: number;
  hasFramework: boolean;
  isPreviewAvailable: boolean;
  previewUrl: string | null | undefined;
  sandboxRunning: boolean;
  activeProjectId: string | undefined;
  projectIdFromUrl: string | null;
  lastBuildSendTimestamp: number | undefined;
  lastPreviewBootedAt: number | null;
  recentBuilderEvents: BuildLogItem[];
  builderEvents?: BuilderEvent[];
  lastFileTargets?: FileTargetItem[];
  /** Count of edits dropped by landing filter this run. */
  landingFilterDroppedCount?: number;
  /** PatchGuard error code when blocked (e.g. PATCH_GUARD_PROTECTED_FILE). */
  patchGuardErrorCode?: string | null;
  /** Count of edits dropped by final landing scope gate this run. */
  landingScopeDroppedCount?: number;
  /** Sample of paths dropped by landing scope (up to 10). */
  landingScopeDroppedPathsSample?: string[];
  /** Patch validation rejections from last apply (client-side). */
  patchValidation?: {
    rejectedPatchItems: Array<{ path: string; reason: string; index?: number }>;
    acceptedCount: number;
    rejectedCount: number;
    patchTotalChars: number;
  } | null;
  /** Server patch firewall meta from builder-chat-openai response. */
  serverPatchValidation?: {
    rejectedPatchItems: Array<{ path: string; reason: string; index?: number }>;
    acceptedCount: number;
    rejectedCount: number;
    patchTotalChars: number;
    intent?: { filesToTouch: string[]; rationale?: string };
    intentRejections?: Array<{ path: string; reason: string }>;
    intentRejectionsCount?: number;
    allowCoreEdits?: boolean;
    coreRejectedCount?: number;
  } | null;
  /** allowCoreEdits toggle (debug-only): current value and setter, persists in localStorage */
  allowCoreEdits?: boolean;
  onAllowCoreEditsChange?: (enabled: boolean) => void;
  /** Last request flags: requestMode ("build"|"fix"|"rules") and ruleChange */
  requestFlags?: { requestMode: string; ruleChange: boolean } | null;
  /** Preflight build (in-memory before persist). */
  preflight?: { ok: boolean; errorCount?: number; firstErrors?: string[]; attemptedRetry?: boolean; retryOutcome?: "succeeded" | "failed" | "no_edits" } | null;
  /** When true, render inline (no fixed positioning) for embedding in a panel */
  inline?: boolean;
};

function formatTs(ts: number | undefined | null): string {
  if (ts == null) return "—";
  const d = new Date(ts);
  return d.toISOString().slice(11, 23) + " " + d.toISOString().slice(0, 10);
}

export function BuilderDebugHUD(props: BuilderDebugHUDProps) {
  const {
    builderStatus,
    waitingForScaffold,
    loadedFileContentsSize,
    hasFramework,
    isPreviewAvailable,
    previewUrl,
    sandboxRunning,
    activeProjectId,
    projectIdFromUrl,
    lastBuildSendTimestamp,
    lastPreviewBootedAt,
    recentBuilderEvents,
    builderEvents = [],
    lastFileTargets = [],
    landingFilterDroppedCount = 0,
    patchGuardErrorCode = null,
    landingScopeDroppedCount = 0,
    landingScopeDroppedPathsSample = [],
    patchValidation = null,
    serverPatchValidation = null,
    preflight = null,
    inline = false,
    allowCoreEdits = false,
    onAllowCoreEditsChange,
    requestFlags = null,
  } = props;

  const copyJson = useCallback(() => {
    const payload = {
      builderStatus,
      waitingForScaffold,
      loadedFileContentsSize,
      hasFramework,
      isPreviewAvailable,
      previewUrl: previewUrl ?? null,
      iframeSrc: previewUrl ?? null,
      sandboxRunning,
      activeProjectId: activeProjectId ?? null,
      projectIdFromUrl,
      lastBuildSendTimestamp: lastBuildSendTimestamp ?? null,
      lastPreviewBootedAt,
      recentBuilderEvents: recentBuilderEvents.map((e) => ({
        id: e.id,
        ts: e.ts,
        status: e.status,
        prompt: e.prompt?.slice(0, 80),
        summary: e.summary?.slice(0, 80),
        error: e.error?.slice(0, 80),
      })),
      builderEvents,
      lastFileTargets,
      _capturedAt: new Date().toISOString(),
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  }, [
    builderStatus,
    waitingForScaffold,
    loadedFileContentsSize,
    hasFramework,
    isPreviewAvailable,
    previewUrl,
    sandboxRunning,
    activeProjectId,
    projectIdFromUrl,
    lastBuildSendTimestamp,
    lastPreviewBootedAt,
    recentBuilderEvents,
    builderEvents,
    lastFileTargets,
  ]);

  const wrapperClass = inline
    ? "flex flex-col gap-3 w-full"
    : "fixed bottom-4 right-4 z-50 flex flex-col gap-3 max-w-sm";
  const contentClass = inline
    ? "rounded-lg border bg-card text-card-foreground p-3 text-xs font-mono space-y-1.5 max-h-[240px] overflow-auto"
    : "rounded-lg border bg-card text-card-foreground shadow-lg p-3 text-xs font-mono space-y-1.5 max-h-[70vh] overflow-auto";

  return (
    <div className={wrapperClass}>
      <div className={contentClass}>
        <div className="font-semibold text-sm border-b pb-1.5 mb-1.5">Builder Debug</div>
        {onAllowCoreEditsChange && (
          <div className="flex items-center gap-2 py-1">
            <label className="text-muted-foreground text-xs cursor-pointer flex items-center gap-2">
              <input
                type="checkbox"
                checked={allowCoreEdits}
                onChange={(e) => {
                  const v = e.target.checked;
                  onAllowCoreEditsChange(v);
                  try { localStorage.setItem("dayone:allowCoreEdits", v ? "true" : "false"); } catch {}
                }}
                className="rounded"
              />
              allowCoreEdits (persists)
            </label>
          </div>
        )}
        {serverPatchValidation && typeof serverPatchValidation.allowCoreEdits === "boolean" && (
          <div><span className="text-muted-foreground">Last request allowCoreEdits</span> {String(serverPatchValidation.allowCoreEdits)}</div>
        )}
        {serverPatchValidation && (serverPatchValidation.coreRejectedCount ?? 0) > 0 && (
          <div className="text-amber-600 dark:text-amber-500">Core rejected: {serverPatchValidation.coreRejectedCount} (src/lib/, src/components/ui/)</div>
        )}
        {requestFlags && (
          <div>
            <span className="text-muted-foreground">Last request</span> requestMode: {requestFlags.requestMode}, ruleChange: {String(requestFlags.ruleChange)}
          </div>
        )}
        <div><span className="text-muted-foreground">builderStatus</span> {builderStatus}</div>
        <div><span className="text-muted-foreground">waitingForScaffold</span> {String(waitingForScaffold)}</div>
        <div><span className="text-muted-foreground">loadedFileContents.size</span> {loadedFileContentsSize}</div>
        <div><span className="text-muted-foreground">hasFramework</span> {String(hasFramework)}</div>
        <div><span className="text-muted-foreground">isPreviewAvailable</span> {String(isPreviewAvailable)}</div>
        <div><span className="text-muted-foreground">previewUrl / iframe src</span> {(previewUrl ?? "—").slice(0, 60)}{previewUrl && previewUrl.length > 60 ? "…" : ""}</div>
        <div><span className="text-muted-foreground">sandboxRunning</span> {String(sandboxRunning)}</div>
        <div><span className="text-muted-foreground">activeProject.id</span> {activeProjectId ?? "—"}</div>
        <div><span className="text-muted-foreground">projectIdFromUrl</span> {projectIdFromUrl ?? "—"}</div>
        <div><span className="text-muted-foreground">last build send</span> {formatTs(lastBuildSendTimestamp)}</div>
        {landingFilterDroppedCount > 0 && (
          <div className="text-amber-600 dark:text-amber-500">Dropped edits: {landingFilterDroppedCount}</div>
        )}
        {landingScopeDroppedCount > 0 && (
          <div className="text-amber-600 dark:text-amber-500">
            Landing scope dropped: {landingScopeDroppedCount}
            {landingScopeDroppedPathsSample.length > 0 && (
              <ul className="mt-0.5 list-disc list-inside text-[10px] opacity-90">
                {landingScopeDroppedPathsSample.map((p, i) => (
                  <li key={`${p}-${i}`} className="truncate" title={p}>{p}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {patchGuardErrorCode && (
          <div className="text-destructive">Blocked by PatchGuard: {patchGuardErrorCode}</div>
        )}
        {patchValidation && (patchValidation.rejectedCount > 0 || patchValidation.acceptedCount > 0) && (
          <div className="pt-1 border-t mt-1">
            <span className="text-muted-foreground font-medium">Patch Validation</span>
            <div className="mt-1 text-muted-foreground">
              Accepted: {patchValidation.acceptedCount} | Rejected: {patchValidation.rejectedCount} | Total chars: {patchValidation.patchTotalChars}
            </div>
            {patchValidation.rejectedPatchItems.length > 0 && (
              <ul className="mt-1 list-disc list-inside text-amber-600 dark:text-amber-500 space-y-0.5 max-h-20 overflow-y-auto">
                {patchValidation.rejectedPatchItems.map((r, i) => (
                  <li key={`${r.path}-${i}`} className="truncate" title={`${r.path}: ${r.reason}`}>
                    {r.path} — {r.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {preflight && (!preflight.ok || preflight.attemptedRetry) && (
          <div className="pt-1 border-t mt-1">
            <span className={`text-muted-foreground font-medium ${preflight.ok ? "text-green-600 dark:text-green-500" : "text-destructive"}`}>
              {preflight.ok && preflight.retryOutcome === "succeeded" ? "Patch Preflight (retry succeeded)" : "Patch Preflight Failed"}
            </span>
            <div className="mt-1 text-muted-foreground">
              {preflight.attemptedRetry && (
                <>Retry: {preflight.retryOutcome ?? "—"}. </>
              )}
              {!preflight.ok && <>Errors: {preflight.errorCount ?? 0}</>}
            </div>
            {preflight.firstErrors && preflight.firstErrors.length > 0 && (
              <ul className="mt-1 list-disc list-inside text-amber-600 dark:text-amber-500 space-y-0.5 max-h-24 overflow-y-auto text-[10px]">
                {preflight.firstErrors.map((err, i) => (
                  <li key={i} className="truncate" title={err}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {serverPatchValidation && (serverPatchValidation.rejectedCount > 0 || serverPatchValidation.acceptedCount > 0) && (
          <div className="pt-1 border-t mt-1">
            <span className="text-muted-foreground font-medium">Server Patch Firewall</span>
            <div className="mt-1 text-muted-foreground">
              Accepted: {serverPatchValidation.acceptedCount} | Rejected: {serverPatchValidation.rejectedCount} | Total chars: {serverPatchValidation.patchTotalChars}
            </div>
            {serverPatchValidation.rejectedPatchItems.length > 0 && (
              <ul className="mt-1 list-disc list-inside text-amber-600 dark:text-amber-500 space-y-0.5 max-h-20 overflow-y-auto">
                {serverPatchValidation.rejectedPatchItems.map((r, i) => (
                  <li key={`srv-${r.path}-${i}`} className="truncate" title={`${r.path}: ${r.reason}`}>
                    {r.path} — {r.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {serverPatchValidation?.intent && (
          <div className="pt-1 border-t mt-1">
            <span className="text-muted-foreground font-medium">Intent</span>
            <div className="mt-1 text-muted-foreground">
              filesToTouch: {serverPatchValidation.intent.filesToTouch.join(", ")}
              {serverPatchValidation.intent.rationale && (
                <p className="mt-0.5 text-[10px] opacity-90">Rationale: {serverPatchValidation.intent.rationale}</p>
              )}
            </div>
            {(serverPatchValidation.intentRejectionsCount ?? 0) > 0 && (
              <div className="mt-1 text-amber-600 dark:text-amber-500">
                Patch items rejected (not in intent): {serverPatchValidation.intentRejectionsCount}
              </div>
            )}
          </div>
        )}
        <div><span className="text-muted-foreground">last preview boot</span> {formatTs(lastPreviewBootedAt)}</div>
        {lastFileTargets.length > 0 && (
          <div className="pt-1 border-t mt-1">
            <span className="text-muted-foreground">Last file targets</span> ({lastFileTargets.length})
            <ul className="mt-1 list-disc list-inside text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto">
              {lastFileTargets.slice(0, 12).map((t, i) => (
                <li key={`${t.path}-${i}`}>
                  {t.path} ({t.action}){t.rationale ? ` — ${String(t.rationale).slice(0, 30)}…` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="pt-1 border-t mt-1">
          <span className="text-muted-foreground">Event log</span> ({builderEvents.length})
          {builderEvents.length > 0 && (
            <ul className="mt-1 list-disc list-inside text-muted-foreground space-y-0.5 max-h-32 overflow-y-auto">
              {builderEvents.slice(-15).map((e, i) => (
                <li key={`${e.ts}-${i}`}>
                  {e.type}
                  {e.data !== undefined && typeof e.data === "object" && e.data !== null && "error" in e.data
                    ? ` — ${String((e.data as { error?: string }).error ?? "").slice(0, 40)}…`
                    : typeof e.data === "number"
                      ? ` ${e.data}`
                      : ""}
                  {" "}
                  @ {formatTs(e.ts)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <Button size="sm" variant="secondary" className="gap-2 w-full" onClick={copyJson}>
        <Copy className="h-3.5 w-3.5" />
        Copy Debug JSON
      </Button>
    </div>
  );
}
